import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppTaskTips } from "./AppTaskTips";

// AppActivity - one contact's recent activity as `index.json` carries it (`act` on a contact row), and
// the ONE rule that says a contact has "gone quiet". Read by BOTH halves: the service writes the row
// (`ActivityIndex`) and builds the reconnect task from `IsQuiet` (`TaskStateOverlay`, tasks on); the
// app's Overview reads the same row and asks the same `IsQuiet`, so the Overview's "Gone quiet" list
// and the Tasks tab's "Reconnect" group can never disagree (`ActivityContract.test.ts`).
//
// ⚠ RULES ONLY - NO `window`, NO `XMLHttpRequest`: the Bridge suite imports this file (the trap
// `AppTaskApi`'s header records - a transport here passes under tsx and fails under tsc).
//
// ==================================================================================================
// !! THE NUMBERS (owner-approved #3/#4, 2026-09-27) - exact, counted by the service, never by a model
// ==================================================================================================
//
// - `d30` / `d90`: messages (both sides) in the 30 / 90 days up to the rebuild (`index.json`'s
//   `builtIso`), counted from every message's own time (`ChatActivity.CountBetween`).
// - `p180`: messages in the 180 days BEFORE those 30 - "their normal level" over the prior six months.
// ⚠ THREE INTEGERS ON PURPOSE: `index.json` is downloaded on every visit, so no averages, no months,
// no dates (the row already has `lastIso` and `daysSinceLast`).
//
// ==================================================================================================
// !! "GONE QUIET" (`IsQuiet`) - tuned 2026-09-27, every threshold a named constant
// ==================================================================================================
//
// All of: (1) a normal level worth missing - at least `QuietMinMonthly` (8) messages a month over the
// prior six months (`p180` >= 48); (2) the last 30 days below `QuietShare` (25%) of that monthly level
// (`d30 * 24 < p180`, integer arithmetic, no rounding); (3) nothing for MORE than `QuietMinDays` (21)
// days; (4) the conversation is not `owed` (the reply task already covers it) and not `concluded` (what
// it existed for has finished - a holiday booking is not a friendship to rekindle).
// ⚠ A chat that stopped more than 210 days ago has `p180` 0 and is never "quiet" - it is history.

/** One contact's activity numbers. */
export class AppActivity {
    /** Messages in the last 30 days (both sides). */
    public D30: number = 0;
    /** Messages in the last 90 days. */
    public D90: number = 0;
    /** Messages in the 180 days before the last 30 - the normal level. */
    public P180: number = 0;
}

export class AppActivityRules {
    /** The row field. ⛔ Pinned by `ActivityContract.test.ts` against the writer. */
    public static readonly Field: string = "act";
    public static readonly RecentDays: number = 30;
    public static readonly WideDays: number = 90;
    public static readonly PriorDays: number = 180;
    /** Months in `PriorDays` - the divisor of the monthly level. */
    public static readonly PriorMonths: number = 6;
    public static readonly QuietMinMonthly: number = 8;
    /** The last 30 days must be below this share of the monthly level. */
    public static readonly QuietShare: number = 0.25;
    public static readonly QuietMinDays: number = 21;

    /** The row's `act` object. */
    public static Json = (Act: AppActivity): Record<string, number> => {
        if (getCoreUtils().KErrorMode === true) { return {}; }
        try {
            return { d30: Act.D30, d90: Act.D90, p180: Act.P180 };
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppActivityRules.Json");
            return {};
        }
    };

    /**
     * The row's `act`, or null when there is none or it is not three non-negative whole numbers - an
     * older index, or a field this build does not understand. ⚠ STRICT: a half-read object would let
     * `IsQuiet` decide on a 0 nobody wrote.
     */
    public static In = (Raw: unknown): AppActivity | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Bag: Record<string, unknown> = Raw as Record<string, unknown>;
            if (AppActivityRules.Count(Bag.d30) === false || AppActivityRules.Count(Bag.d90) === false
                || AppActivityRules.Count(Bag.p180) === false) { return null; }
            const Out: AppActivity = new AppActivity();
            Out.D30 = Bag.d30 as number;
            Out.D90 = Bag.d90 as number;
            Out.P180 = Bag.p180 as number;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppActivityRules.In");
            return null;
        }
    };

    /**
     * Has this contact gone quiet against their own normal level? See the header for the four rules.
     * `DaysSinceLast` is the row's `daysSinceLast` (whole days since the last message, either side).
     */
    public static IsQuiet = (Act: AppActivity | null, Standing: string, DaysSinceLast: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Act === null) { return false; }
            if (Standing === "owed" || Standing === "concluded") { return false; }
            if (Act.P180 < AppActivityRules.QuietMinMonthly * AppActivityRules.PriorMonths) { return false; }
            // d30 < QuietShare * p180 / PriorMonths, kept in whole numbers: d30 * 6 / 0.25 < p180.
            if (Act.D30 * AppActivityRules.PriorMonths / AppActivityRules.QuietShare >= Act.P180) { return false; }
            return DaysSinceLast > AppActivityRules.QuietMinDays;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppActivityRules.IsQuiet");
            return false;
        }
    };

    /** The normal level, messages a month, rounded (for a sentence - the rule never rounds). */
    public static Monthly = (Act: AppActivity): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            return Math.round(Act.P180 / AppActivityRules.PriorMonths);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppActivityRules.Monthly");
            return 0;
        }
    };

    /** "Reconnect with Anna?" - the reconnect task's label; "Reconnect?" names nobody when there is no name. */
    public static ReconnectLabel = (Title: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const First: string = AppTaskTips.FirstName(nz(Title));
            if (First.length === 0 && nz(Title).trim().length > 0) { return "Reconnect with " + nz(Title).trim() + "?"; }
            if (First.length === 0) { return "Reconnect?"; }
            return "Reconnect with " + First + "?";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppActivityRules.ReconnectLabel");
            return "";
        }
    };

    /** "Usually about 14 messages a month - last one 34 days ago." Plain text; the caller escapes it. */
    public static QuietWhy = (Act: AppActivity, DaysSinceLast: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Monthly: number = AppActivityRules.Monthly(Act);
            let Unit: string = "messages";
            if (Monthly === 1) { Unit = "message"; }
            return "Usually about " + Monthly.toLocaleString() + " " + Unit + " a month - last one "
                + String(DaysSinceLast) + " days ago.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppActivityRules.QuietWhy");
            return "";
        }
    };

    /**
     * A whole number of messages, 0 or more.
     * DOCUMENTED EXCEPTION: pure check. ERROR-SUPPRESSED-JUSTIFIED: false (not a count) IS the safe answer.
     */
    private static Count = (Raw: unknown): boolean => {
        return typeof Raw === "number" && Number.isInteger(Raw) === true && Raw >= 0;
    };
}
